import { Pipe, PipeTransform } from '@angular/core';
import {
  createChartData,
  lowCodeNoCodeAdoptionTransforms,
  copilotsAdoptionTransforms,
  passedFailedCategories,
  m365copilotPluginTypes,
  provideChartData,
  violationCategories,
  violationsOvertimeCategories,
  owaspRiskCategoriesForAi,
  enterpriseAiViolationCategories,
  platformCategories,
} from '../common/chartUtils';
import { emptyArray } from '../common/functionalUtils';
import {
  AggregationDict,
  AggregationsModel,
  AggregationsTrend,
  ChartsAggregationType,
  OpenViolations,
  RawAggregationCategorySum,
  RulesAmounts,
  ViolationsCycleTime,
  PluginTypes,
  OwaspRisksForAi,
  toAggregationCategorySumWithExtraInfo,
} from '../models/api/aggregations.model';
import {
  AggregationTrendSeries,
  IChartData,
  AggregationCategorySumWithExtraInfo,
  ResourceViolations as ResourceViolationsSum,
  addAllMitreCategories,
  addAllOwaspCategories,
  addAllOwaspLlmCategories,
  addAllOwaspAsiCategories,
  addAllOwaspNhiCategories,
  byDisplayName,
  rulesAmountTempAdapter,
  toMitreDisplayName,
  toOwaspDisplayName,
  toOwaspLlmDisplayName,
  toOwaspAsiDisplayName,
  toOwaspNhiDisplayName,
  toResourceViolations,
  violationCycleTimeMedian,
  RawAggregationCategorySumDetails,
} from '../models/api/dashboards.model';
import { BeautifyDisplayStringPipe } from './beautify-display-string/beautify-display-string.pipe';

type AggregationTypeToSeriesType = {
  // Main
  [ChartsAggregationType.owaspBySeverities]: ResourceViolationsSum;
  [ChartsAggregationType.owaspLlmBySeverities]: ResourceViolationsSum;
  [ChartsAggregationType.owaspAsiBySeverities]: ResourceViolationsSum;
  [ChartsAggregationType.owaspNhiBySeverities]: ResourceViolationsSum;
  [ChartsAggregationType.mitreBySeverities]: ResourceViolationsSum;
  [ChartsAggregationType.openViolationsSeverityDistribution]: IChartData;
  [ChartsAggregationType.rulesAmounts]: IChartData;
  [ChartsAggregationType.lowCodeNoCodeAdoption]: AggregationTrendSeries;
  [ChartsAggregationType.riskMappingScore]: AggregationDict;
  [ChartsAggregationType.agentsByPlatform]: IChartData;

  // Risk
  [ChartsAggregationType.violationsOvertime]: AggregationTrendSeries;
  [ChartsAggregationType.topRiskyEnvironments]: ResourceViolationsSum;
  [ChartsAggregationType.topRiskyUsers]: ResourceViolationsSum;
  [ChartsAggregationType.violationsCycleTime]: IChartData;

  // AI
  [ChartsAggregationType.copilotsAdoption]: AggregationTrendSeries;
  [ChartsAggregationType.owaspRiskDistributionForAi]: IChartData;
  [ChartsAggregationType.openViolationsSeverityDistributionForAi]: IChartData;
  [ChartsAggregationType.m365copilotPluginTypesDistribution]: IChartData;
  [ChartsAggregationType.m365copilotTopPlugins]: AggregationCategorySumWithExtraInfo;
};

type SeriesDataType<T extends keyof typeof ChartsAggregationType> =
  AggregationTypeToSeriesType[(typeof ChartsAggregationType)[T]];

@Pipe({
  name: 'chartData',
})
export class ChartsProviderPipe implements PipeTransform {
  constructor(private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe) {}

  public transform<T extends keyof typeof ChartsAggregationType>(
    model: AggregationsModel | null,
    typeLiteral: T
  ): SeriesDataType<T>[] {
    const typeSafe = (array: unknown[]) => array as AggregationTypeToSeriesType[(typeof ChartsAggregationType)[T]][];

    if (model == null) return emptyArray<SeriesDataType<T>>();

    const type = ChartsAggregationType[typeLiteral];
    const data = model.aggregationsData[type].data;

    switch (type) {
      case ChartsAggregationType.violationsOvertime:
        return typeSafe(provideChartData(data as AggregationsTrend, violationsOvertimeCategories));
      case ChartsAggregationType.topRiskyEnvironments:
        return typeSafe((data as RawAggregationCategorySum[]).map(toResourceViolations()));
      case ChartsAggregationType.topRiskyUsers:
        return typeSafe((data as RawAggregationCategorySum[]).map(toResourceViolations()));
      case ChartsAggregationType.owaspBySeverities:
        return typeSafe(
          (data as RawAggregationCategorySum[])
            .also(addAllOwaspCategories)
            .map(toResourceViolations(toOwaspDisplayName))
            .sort(byDisplayName)
        );
      case ChartsAggregationType.owaspLlmBySeverities:
        return typeSafe(
          (data as RawAggregationCategorySum[])
            .also(addAllOwaspLlmCategories)
            .map(toResourceViolations(toOwaspLlmDisplayName))
            .sort(byDisplayName)
        );
      case ChartsAggregationType.owaspAsiBySeverities:
        return typeSafe(
          (data as RawAggregationCategorySum[])
            .also(addAllOwaspAsiCategories)
            .map(toResourceViolations(toOwaspAsiDisplayName))
            .sort(byDisplayName)
        );
      case ChartsAggregationType.owaspNhiBySeverities:
        return typeSafe(
          (data as RawAggregationCategorySum[])
            .also(addAllOwaspNhiCategories)
            .map(toResourceViolations(toOwaspNhiDisplayName))
            .sort(byDisplayName)
        );
      case ChartsAggregationType.mitreBySeverities:
        return typeSafe(
          (data as RawAggregationCategorySum[])
            .also(addAllMitreCategories)
            .map(toResourceViolations(toMitreDisplayName))
            .sort(byDisplayName)
        );
      case ChartsAggregationType.violationsCycleTime:
        return typeSafe(createChartData(data as ViolationsCycleTime, violationCategories, violationCycleTimeMedian));
      case ChartsAggregationType.openViolationsSeverityDistribution:
        return typeSafe(createChartData(data as OpenViolations, violationCategories));
      case ChartsAggregationType.rulesAmounts:
        return typeSafe(createChartData(rulesAmountTempAdapter(data as RulesAmounts), passedFailedCategories));
      case ChartsAggregationType.lowCodeNoCodeAdoption:
        return typeSafe(
          provideChartData(
            data as AggregationsTrend,
            lowCodeNoCodeAdoptionTransforms,
            '',
            this._beautifyDisplayStringPipe
          )
        );
      case ChartsAggregationType.riskMappingScore:
        return typeSafe([data as AggregationDict]);
      case ChartsAggregationType.copilotsAdoption:
        return typeSafe(provideChartData(data as AggregationsTrend, copilotsAdoptionTransforms));
      case ChartsAggregationType.owaspRiskDistributionForAi:
        return typeSafe(createChartData(data as OwaspRisksForAi, owaspRiskCategoriesForAi));
      case ChartsAggregationType.openViolationsSeverityDistributionForAi:
        return typeSafe(createChartData(data as OpenViolations, enterpriseAiViolationCategories));
      case ChartsAggregationType.m365copilotPluginTypesDistribution:
        return typeSafe(createChartData(data as PluginTypes, m365copilotPluginTypes));
      case ChartsAggregationType.m365copilotTopPlugins:
        return typeSafe((data as RawAggregationCategorySumDetails[]).map(toAggregationCategorySumWithExtraInfo));
      case ChartsAggregationType.agentsByPlatform:
        return typeSafe(createChartData(data as AggregationDict, platformCategories));
      default:
        throw new Error(`ChartsProviderPipe: ${type} not supported`);
    }
  }
}
